/**
 * Camera scanning through html5-qrcode, mounted into any element.
 *
 * Continuous mode keeps the camera running and reports each new code; a code seen again within
 * RepeatSuppressMS is ignored, so holding a label in front of the camera reports it once.
 */

const RepeatSuppressMS = 1500;

class CameraScanSource
{
	/**
	 * @param {Object} pOptions
	 * @param {string} pOptions.ElementID - Id of the element the camera preview renders into.
	 * @param {(pText: string) => void} pOptions.onScan
	 * @param {(pError: any) => void} [pOptions.onError]
	 */
	constructor(pOptions)
	{
		this.elementID = pOptions.ElementID;
		this.onScan = pOptions.onScan;
		this.onError = pOptions.onError || (() => {});
		this.scanner = null;
		this.running = false;
		this.lastText = '';
		this.lastTime = 0;
	}

	/**
	 * @return {boolean} true when the browser can open a camera at all
	 */
	static isSupported()
	{
		return (typeof navigator !== 'undefined') && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
	}

	/**
	 * @return {Promise<void>}
	 */
	async start()
	{
		if (this.running)
		{
			return;
		}
		const { Html5Qrcode, Html5QrcodeSupportedFormats } = require('html5-qrcode');
		this.scanner = new Html5Qrcode(this.elementID, { verbose: false, formatsToSupport: [ Html5QrcodeSupportedFormats.QR_CODE ] });
		const tmpBox = (pWidth, pHeight) =>
		{
			const tmpSide = Math.floor(Math.min(pWidth, pHeight) * 0.7);
			return { width: tmpSide, height: tmpSide };
		};
		try
		{
			await this.scanner.start({ facingMode: 'environment' }, { fps: 15, qrbox: tmpBox }, (pText) => this.handleText(pText), () => {});
			this.running = true;
		}
		catch (pError)
		{
			this.scanner = null;
			this.onError(pError);
			throw pError;
		}
	}

	/**
	 * @param {string} pText
	 */
	handleText(pText)
	{
		const tmpNow = Date.now();
		if (pText === this.lastText && (tmpNow - this.lastTime) < RepeatSuppressMS)
		{
			this.lastTime = tmpNow;
			return;
		}
		this.lastText = pText;
		this.lastTime = tmpNow;
		this.onScan(pText);
	}

	/**
	 * @return {Promise<void>}
	 */
	async stop()
	{
		if (!this.scanner)
		{
			return;
		}
		const tmpScanner = this.scanner;
		this.scanner = null;
		this.running = false;
		try
		{
			await tmpScanner.stop();
			tmpScanner.clear();
		}
		catch (pError)
		{
			this.onError(pError);
		}
	}
}

module.exports = CameraScanSource;
